import {
  EditMenuLink,
  FirestickConfig,
  FooterRow,
  Icon,
  MenuLink,
  Stores,
  Streaming,
} from "../types";
import { makeAutoObservable } from "mobx";
import api from "../services/api";

export class ConfigStore {
  stores: Stores;

  iconsList: Icon[] = [];

  menuLinksList: MenuLink[] = [];

  footerRows: FooterRow[] = [];

  streaming: Streaming = {} as Streaming;

  firestickConfig: FirestickConfig = {} as FirestickConfig;

  isLoading = false;

  constructor(stores: Stores) {
    this.stores = stores;

    makeAutoObservable(this);
  }

  addIcon = async (data: Icon) => {
    this.setIsLoading(true);
    try {
      let icon = await api.addIcon(data);

      this.setIconsList([icon, ...this.iconsList]);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  deleteIcon = async (id: number) => {
    this.setIsLoading(true);
    try {
      await api.deleteIcon(id);
      this.setIconsList(this.iconsList.filter((x) => x.id !== id));
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  fetchIconsList = async () => {
    this.setIsLoading(true);
    try {
      const iconsList = await api.getIcons();
      this.setIconsList(iconsList);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  addMenuLink = async (data: EditMenuLink) => {
    this.setIsLoading(true);
    try {
      const menuLink = await api.addMenuLink(data);

      this.setMenuLinksList([menuLink, ...this.menuLinksList]);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  updateMenuLink = async (data: EditMenuLink) => {
    this.setIsLoading(true);
    try {
      const updatedMenuLink = await api.updateMenuLink(data);

      this.setMenuLinksList(
        this.menuLinksList.map((menuLink) =>
          menuLink.id === updatedMenuLink.id ? updatedMenuLink : menuLink
        )
      );
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  deleteMenuLink = async (id: number) => {
    this.setIsLoading(true);
    try {
      await api.deleteMenuLink(id);
      this.setMenuLinksList(this.menuLinksList.filter((x) => x.id !== id));
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  fetchMenuLinksList = async () => {
    this.setIsLoading(true);
    try {
      const menuLinksList = await api.getMenuLinks();
      this.setMenuLinksList(menuLinksList);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  fetchFooterRows = async () => {
    this.setIsLoading(true);
    try {
      const footerRows = await api.getFooterRows();
      this.setFooterRows(footerRows);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  addFooterRow = async (data: FooterRow) => {
    this.setIsLoading(true);
    try {
      const footerRow = await api.addFooterRow(data);
      this.setFooterRows(
        [footerRow, ...this.footerRows].sort(
          (a, b) => a.orderNumber - b.orderNumber
        )
      );
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  updateFooterRow = async (data: FooterRow) => {
    this.setIsLoading(true);
    try {
      const updatedFooterRow = await api.updateFooterRow(data);

      this.setFooterRows(
        this.footerRows.map((footerRow) =>
          footerRow.id === updatedFooterRow.id ? updatedFooterRow : footerRow
        )
      );
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  deleteFooterRow = async (id: number) => {
    this.setIsLoading(true);
    try {
      await api.deleteFooterRow(id);
      this.setFooterRows(this.footerRows.filter((x) => x.id !== id));
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  fetchStreaming = async () => {
    this.setIsLoading(true);
    try {
      const streaming = await api.getStreaming();
      this.setStreaming(streaming);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  addStreaming = async (data: Streaming) => {
    this.setIsLoading(true);
    try {
      const streaming = await api.addStreaming(data);
      this.setStreaming(streaming);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  updateStreaming = async (data: Streaming) => {
    this.setIsLoading(true);
    try {
      const updatedStreaming = await api.updateStreaming(data);

      this.setStreaming(updatedStreaming);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  fetchFirestickConfig = async () => {
    this.setIsLoading(true);
    try {
      const firestickConfig = await api.getFirestickConfig();
      this.setFirestickConfig(firestickConfig);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  addFirestickConfig = async (data: FirestickConfig) => {
    this.setIsLoading(true);
    try {
      const firestickConfig = await api.addFirestickConfig(data);
      this.setFirestickConfig(firestickConfig);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  updateFirestickConfig = async (data: FirestickConfig) => {
    this.setIsLoading(true);
    try {
      const updatedFirestickConfig = await api.updateFirestickConfig(data);

      this.setFirestickConfig(updatedFirestickConfig);
    } catch (err) {
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  };

  // Setters

  setIconsList = (iconsList: Icon[]) => {
    this.iconsList = iconsList;
  };

  setMenuLinksList = (menuLinksList: MenuLink[]) => {
    this.menuLinksList = menuLinksList;
  };

  setFooterRows = (footerRows: FooterRow[]) => {
    this.footerRows = footerRows;
  };

  setStreaming = (streaming: Streaming) => {
    this.streaming = streaming;
  };

  setFirestickConfig = (firestickConfig: FirestickConfig) => {
    this.firestickConfig = firestickConfig;
  };

  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };
}
